import React, { useState, useEffect } from 'react';
import './EmailVerification.css';

function EmailVerification() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState(null); // null, loading, success, error
  const [message, setMessage] = useState('');
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    checkVerificationStatus();
  }, []);

  const checkVerificationStatus = async () => {
    try {
      const token = localStorage.getItem('accessToken'); // Fixed: was 'token'
      const response = await fetch(`/api/email-verification/status`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setVerificationStatus(data);
        setEmail(data.email || '');
      }
    } catch (error) {
      console.error('Error checking verification status:', error);
    }
  };

  const handleSendVerification = async (e) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setStatus('error');
      setMessage('Please enter a valid email address');
      return;
    }

    setStatus('loading');
    setMessage('');

    try {
      const token = localStorage.getItem('accessToken'); // Fixed: was 'token'
      const response = await fetch(`/api/email-verification/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage('Verification email sent! Please check your inbox.');
        setIsEditing(false);
        checkVerificationStatus();
      } else {
        setStatus('error');
        setMessage(data.error || 'Failed to send verification email');
      }
    } catch (error) {
      console.error('Error sending verification:', error);
      setStatus('error');
      setMessage('Failed to send verification email. Please try again.');
    }
  };

  const handleResend = async () => {
    setStatus('loading');
    setMessage('');

    try {
      const token = localStorage.getItem('accessToken'); // Fixed: was 'token'
      const response = await fetch(`/api/email-verification/resend`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage('Verification email resent! Please check your inbox.');
      } else {
        setStatus('error');
        setMessage(data.error || 'Failed to resend verification email');
      }
    } catch (error) {
      console.error('Error resending verification:', error);
      setStatus('error');
      setMessage('Failed to resend verification email. Please try again.');
    }
  };

  if (!verificationStatus) {
    return <div className="email-verification-loading">Loading...</div>;
  }

  if (verificationStatus.verified) {
    return (
      <div className="email-verification-section">
        <div className="verification-status verified">
          <span className="status-icon">✓</span>
          <div className="status-content">
            <h3>Email Verified</h3>
            <p>{verificationStatus.email}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="email-verification-section">
      <div className="verification-header">
        <h3>📧 Email Verification</h3>
        <p>Your email should be verified from registration. If not, you can verify it here.</p>
      </div>

      {!verificationStatus.hasEmail || isEditing ? (
        <form onSubmit={handleSendVerification} className="email-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>
            <input
              type="email"
              id="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your.email@example.com"
              required
            />
          </div>

          <div className="form-actions">
            <button type="submit" className="btn-primary" disabled={status === 'loading'}>
              {status === 'loading' ? 'Sending...' : 'Send Verification Email'}
            </button>
            {isEditing && (
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setIsEditing(false);
                  setEmail(verificationStatus.email || '');
                }}
              >
                Cancel
              </button>
            )}
          </div>
        </form>
      ) : (
        <div className="verification-status pending">
          <span className="status-icon">⏳</span>
          <div className="status-content">
            <h4>Verification Pending</h4>
            <p>{verificationStatus.email}</p>
            <div className="status-actions">
              <button onClick={handleResend} className="btn-link" disabled={status === 'loading'}>
                {status === 'loading' ? 'Sending...' : verificationStatus.emailSent ? 'Resend Verification Email' : 'Send Verification Email'}
              </button>
              <button onClick={() => setIsEditing(true)} className="btn-link">
                Change Email
              </button>
            </div>
          </div>
        </div>
      )}

      {message && (
        <div className={`message ${status}`}>
          {message}
        </div>
      )}

      <div className="verification-info">
        <h4>Why is email important?</h4>
        <ul>
          <li>✓ Recover your account if you lose access</li>
          <li>✓ Receive important security notifications</li>
          <li>✓ Get updates about your connections and communities</li>
        </ul>
      </div>
    </div>
  );
}

export default EmailVerification;
